import React, { useState, useRef } from 'react';
import './FileUpload.css';
import getRecaptchaToken from '../../reCAPTCHABase';

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB
const MAX_FILES = 5;
const ALLOWED_EXTENSIONS = ['png', 'jpg', 'jpeg', 'pdf', 'ai', 'eps', 'svg'];

function FileUpload({ uploadedArtwork, onUploadedArtworkChange }) {
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const files = uploadedArtwork || [];

  const getFileExtension = filename => {
    return filename
      .split('.')
      .pop()
      .toLowerCase();
  };

  const getMimeType = file => {
    // For AI and EPS files, browsers may report as application/postscript
    const ext = getFileExtension(file.name);
    if (ext === 'ai') return 'application/illustrator';
    if (ext === 'eps') return 'application/postscript';
    return file.type;
  };

  const validateFile = file => {
    const ext = getFileExtension(file.name);

    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return 'File type not allowed. Please upload PNG, JPG, PDF, AI, EPS, or SVG files.';
    }

    if (file.size > MAX_FILE_SIZE) {
      return 'File is too large. Maximum size is 25MB.';
    }

    return null;
  };

  // Create a local preview URL for image files
  const createPreviewUrl = file => {
    const ext = getFileExtension(file.name);
    const imageExtensions = ['png', 'jpg', 'jpeg', 'svg'];
    if (imageExtensions.includes(ext)) {
      return URL.createObjectURL(file);
    }
    return null;
  };

  const uploadFile = async file => {
    const validationError = validateFile(file);
    if (validationError) {
      throw new Error(validationError);
    }

    const mimeType = getMimeType(file);

    // Create preview URL before uploading (for images)
    const previewUrl = createPreviewUrl(file);

    // Get reCAPTCHA token for upload verification
    var recaptchaData = {};
    if (typeof window.grecaptcha !== 'undefined') {
      try {
        recaptchaData = await getRecaptchaToken('artwork_upload');
      } catch (e) {
        // Continue without recaptcha if it fails
      }
    }

    // Get presigned URL from backend
    const csrfToken = document.querySelector('meta[name="csrf-token"]');
    const presignedResponse = await fetch('/artwork_uploads', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken ? csrfToken.content : '',
      },
      body: JSON.stringify({
        filename: file.name,
        content_type: mimeType,
        file_size: file.size,
        'g-recaptcha-response': recaptchaData['g-recaptcha-response'] || '',
      }),
    });

    if (!presignedResponse.ok) {
      const errorData = await presignedResponse.json();
      // Clean up preview URL if upload fails
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      throw new Error(errorData.error || 'Failed to get upload URL');
    }

    const { url, key } = await presignedResponse.json();

    // Upload directly to S3
    const uploadResponse = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': mimeType,
      },
      body: file,
    });

    if (!uploadResponse.ok) {
      // Clean up preview URL if upload fails
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      throw new Error('Failed to upload file');
    }

    const uploadedFile = {
      s3_key: key,
      filename: file.name,
      content_type: mimeType,
      size: file.size,
      preview_url: previewUrl,
    };
    return uploadedFile;
  };

  const handleFiles = async fileList => {
    const filesToUpload = Array.from(fileList);

    if (files.length + filesToUpload.length > MAX_FILES) {
      setError('You can only upload up to ' + MAX_FILES + ' files.');
      return;
    }

    setError(null);
    setUploading(true);

    // Track successful uploads incrementally to avoid losing them if one fails
    var successCount = 0;
    var lastError = null;
    var currentFiles = files || [];

    for (var i = 0; i < filesToUpload.length; i++) {
      try {
        var uploadedFile = await uploadFile(filesToUpload[i]);
        // Update state immediately after each successful upload
        // This ensures successful uploads are saved even if later ones fail
        currentFiles = [...currentFiles, uploadedFile];
        onUploadedArtworkChange(currentFiles);
        successCount++;
      } catch (err) {
        lastError = err;
        // Continue trying to upload remaining files
      }
    }

    // Track upload in Google Analytics
    if (successCount > 0 && typeof window.gtag === 'function') {
      window.gtag('event', 'artwork_uploaded', {
        event_category: 'engagement',
        value: successCount,
      });
    }

    // Show error if any uploads failed
    if (lastError) {
      if (successCount > 0) {
        setError(successCount + ' file(s) uploaded successfully, but: ' + lastError.message);
      } else {
        setError(lastError.message);
      }
    }

    setUploading(false);
  };

  const handleDragOver = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileInputChange = e => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
    // Reset input so the same file can be selected again
    e.target.value = '';
  };

  const handleRemoveFile = index => {
    // Revoke the blob URL to prevent memory leaks
    const fileToRemove = files[index];
    if (fileToRemove && fileToRemove.preview_url) {
      URL.revokeObjectURL(fileToRemove.preview_url);
    }
    const newFiles = files.filter((_, i) => i !== index);
    onUploadedArtworkChange(newFiles);
  };

  const handleBrowseClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const formatFileSize = bytes => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Get file type icon for non-image files
  const getFileTypeIcon = filename => {
    const ext = getFileExtension(filename);
    switch (ext) {
      case 'pdf':
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
            <text
              x="8"
              y="17"
              fontSize="6"
              fill="currentColor"
              stroke="none"
              fontWeight="bold"
            >
              PDF
            </text>
          </svg>
        );
      case 'ai':
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
            <text
              x="9"
              y="17"
              fontSize="6"
              fill="currentColor"
              stroke="none"
              fontWeight="bold"
            >
              AI
            </text>
          </svg>
        );
      case 'eps':
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
            <text
              x="7"
              y="17"
              fontSize="6"
              fill="currentColor"
              stroke="none"
              fontWeight="bold"
            >
              EPS
            </text>
          </svg>
        );
      default:
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
          </svg>
        );
    }
  };

  // Check if file is an image type that can be previewed
  const isPreviewableImage = file => {
    const ext = getFileExtension(file.filename);
    return ['png', 'jpg', 'jpeg', 'svg'].includes(ext);
  };

  return (
    <div className="file-upload">
      <label className="file-upload-label" htmlFor="artwork-file-input">
        Upload Your Artwork
      </label>
      <p className="file-upload-description">
        Upload your design files (PNG, JPG, PDF, AI, EPS, SVG). Max 25MB per
        file, up to 5 files.
      </p>

      <div
        role="button"
        tabIndex={0}
        className={
          'file-upload-dropzone' +
          (isDragging ? ' file-upload-dropzone--dragging' : '') +
          (uploading ? ' file-upload-dropzone--uploading' : '')
        }
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleBrowseClick}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleBrowseClick(e);
          }
        }}
      >
        <input
          ref={fileInputRef}
          id="artwork-file-input"
          type="file"
          className="file-upload-input"
          accept=".png,.jpg,.jpeg,.pdf,.ai,.eps,.svg"
          multiple
          onChange={handleFileInputChange}
        />

        {uploading ? (
          <div className="file-upload-uploading">
            <div className="file-upload-spinner"></div>
            <span>Uploading...</span>
          </div>
        ) : (
          <>
            <div className="file-upload-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="17 8 12 3 7 8"></polyline>
                <line x1="12" y1="3" x2="12" y2="15"></line>
              </svg>
            </div>
            <p className="file-upload-text">
              <span className="file-upload-text-main">
                Drag and drop files here
              </span>
              <span className="file-upload-text-or">or</span>
              <span className="file-upload-text-browse">Browse files</span>
            </p>
          </>
        )}
      </div>

      {error && <div className="file-upload-error">{error}</div>}

      {files.length > 0 && (
        <div className="file-upload-list">
          {files.map((file, index) => (
            <div key={file.s3_key} className="file-upload-item">
              <div className="file-upload-item-preview">
                {file.preview_url && isPreviewableImage(file) ? (
                  <img
                    src={file.preview_url}
                    alt={file.filename}
                    className="file-upload-item-thumbnail"
                  />
                ) : (
                  <div className="file-upload-item-icon">
                    {getFileTypeIcon(file.filename)}
                  </div>
                )}
              </div>
              <div className="file-upload-item-info">
                <span className="file-upload-item-name">{file.filename}</span>
                <span className="file-upload-item-size">
                  {formatFileSize(file.size)}
                </span>
              </div>
              <button
                type="button"
                className="file-upload-item-remove"
                onClick={() => handleRemoveFile(index)}
                aria-label="Remove file"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default FileUpload;
